import CONSTANTS from '@/constants'
import { containsControlCharacters } from './checkoutNavigation'

const { AUTH_PROTOCOL_KEYS: CALLBACK_PARAMS } = CONSTANTS

const CLIENT_STATE_STORAGE_KEY = 'checkout:federated-login-state'
const CLIENT_STATE_TTL_MS = 10 * 60 * 1000
const CALLBACK_HASH_PARAM_PATTERN = new RegExp(
  `(?:^|[^A-Za-z0-9_])(?:${CALLBACK_PARAMS.join('|')})(?:=|&|$)`,
  'i'
)

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
type CallbackResult = {
  hasCallbackParams: boolean
  isValid: boolean
  accessToken: string | null
  error: string | null
}

type StoredClientState = {
  value: string
  createdAt: number
}

type SecureRandom = {
  getRandomValues: (bytes: Uint8Array) => Uint8Array
}

type CallbackInitialization = {
  pathname: string
  search: string
  hash: string
  setAccessToken: (token: string) => void
  replaceUrl: (url: string) => void
  storage?: StorageLike | null
  now?: number
}

const HISTORY_URL_KEYS = ['back', 'current', 'forward'] as const

let pendingCallback: CallbackResult | null = null

function hasFederatedCallbackParams(search: string, hash: string) {
  const queryParams = new URLSearchParams(search)
  const fragmentParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
  return (
    CALLBACK_PARAMS.some(param => queryParams.has(param)) ||
    CALLBACK_PARAMS.some(param => fragmentParams.has(param))
  )
}

/**
 * Reads sessionStorage without allowing browser privacy/security restrictions
 * to break callback initialization.
 */
function getSessionStorage(): StorageLike | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

/**
 * Generates 256 bits of cryptographically secure client state for correlating
 * a callback with the browser tab that initiated federated login.
 */
export function createFederatedLoginClientState(cryptoObject: SecureRandom = crypto): string {
  const bytes = new Uint8Array(32)
  cryptoObject.getRandomValues(bytes)
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
}

/**
 * Stores the client state and creation time in sessionStorage. Tab-scoped
 * storage and the timestamp support browser correlation and the ten-minute TTL.
 */
export function storeFederatedLoginClientState(
  value: string,
  storage: StorageLike | null = getSessionStorage(),
  now: number = Date.now()
): void {
  if (!storage) throw new Error('Session storage is unavailable')
  storage.setItem(CLIENT_STATE_STORAGE_KEY, JSON.stringify({ value, createdAt: now }))
}

/**
 * Removes pending correlation state when login is abandoned or after a
 * callback attempt. Stored state is intentionally single-use.
 */
export function clearFederatedLoginClientState(storage: StorageLike | null = getSessionStorage()) {
  storage?.removeItem(CLIENT_STATE_STORAGE_KEY)
}

/**
 * Detects and validates a federated callback. A valid result requires the UAA
 * marker, unique fragment parameters, matching 256-bit state, an unexpired
 * timestamp, and either an access token or an error. State is consumed before
 * validation so invalid, expired, and replayed callbacks cannot reuse it.
 */
export function extractFederatedLoginCallback(
  search: string,
  hash: string,
  storage: StorageLike | null = getSessionStorage(),
  now: number = Date.now()
): CallbackResult {
  const queryParams = new URLSearchParams(search)
  const fragmentParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
  const hasCallbackParams = hasFederatedCallbackParams(search, hash)

  if (!hasCallbackParams) {
    return {
      hasCallbackParams: false,
      isValid: false,
      accessToken: null,
      error: null,
    }
  }

  let storedState: StoredClientState | null = null
  try {
    const rawState = storage?.getItem(CLIENT_STATE_STORAGE_KEY)
    storedState = rawState ? JSON.parse(rawState) : null
  } catch {
    storedState = null
  }
  clearFederatedLoginClientState(storage)

  const callbackState = fragmentParams.get('login_state')
  const hasDuplicateCallbackParams =
    CALLBACK_PARAMS.some(param => fragmentParams.getAll(param).length > 1) ||
    CALLBACK_PARAMS.some(param => queryParams.has(param))
  const accessToken = fragmentParams.get('access_token')
  const error = fragmentParams.get('error')
  const isValid =
    fragmentParams.get('uaa_callback') === '1' &&
    !hasDuplicateCallbackParams &&
    typeof storedState?.value === 'string' &&
    /^[a-f0-9]{64}$/.test(storedState.value) &&
    callbackState === storedState.value &&
    Number.isFinite(storedState.createdAt) &&
    now >= storedState.createdAt &&
    now - storedState.createdAt <= CLIENT_STATE_TTL_MS &&
    (Boolean(accessToken) || Boolean(error))

  return {
    hasCallbackParams: true,
    isValid,
    accessToken: isValid ? accessToken : null,
    error: isValid ? error : null,
  }
}

function sanitizeRestoredCallbackHash(hash: string, callbackHash: string) {
  try {
    // URLSearchParams tolerates malformed encoding. Validate that outer layer
    // and decode the restored fragment once before router/tracking startup.
    decodeURIComponent(callbackHash)
    const decodedHash = decodeURIComponent(hash)
    return containsControlCharacters(decodedHash) || CALLBACK_HASH_PARAM_PATTERN.test(decodedHash)
      ? ''
      : hash
  } catch {
    return ''
  }
}

/**
 * Removes callback credentials and state from the browser location while
 * preserving ordinary query values and restoring the pre-login hash supplied
 * through return_hash.
 */
export function stripFederatedLoginCallbackParams(pathname: string, search: string, hash: string) {
  const queryParams = new URLSearchParams(search)
  CALLBACK_PARAMS.forEach(param => queryParams.delete(param))
  const query = queryParams.toString()

  const fragmentParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
  const isCallbackFragment = CALLBACK_PARAMS.some(param => fragmentParams.has(param))
  let restoredHash = hash.startsWith('#') ? hash.slice(1) : hash
  if (isCallbackFragment) {
    if (fragmentParams.has('uaa_callback')) {
      restoredHash = sanitizeRestoredCallbackHash(fragmentParams.get('return_hash') || '', hash)
    } else {
      restoredHash = ''
    }
  }

  return `${pathname}${query ? `?${query}` : ''}${restoredHash ? `#${restoredHash}` : ''}`
}

function sanitizeHistoryUrl(value: string) {
  try {
    const origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin
    const parsed = new URL(value, origin)
    if (!hasFederatedCallbackParams(parsed.search, parsed.hash)) return value

    const sanitized = stripFederatedLoginCallbackParams(parsed.pathname, parsed.search, parsed.hash)
    return /^[A-Za-z][A-Za-z\d+.-]*:/.test(value)
      ? new URL(sanitized, parsed.origin).href
      : sanitized
  } catch {
    return value
  }
}

/**
 * Removes callback credentials from Vue Router URL fields while retaining its
 * position, scroll, and any application-owned history state.
 */
export function sanitizeFederatedLoginHistoryState(state: unknown, currentUrl: string): unknown {
  if (!state || typeof state !== 'object' || Array.isArray(state)) return state

  const routerState = state as Record<string, unknown>

  return HISTORY_URL_KEYS.reduce(
    (sanitized, key) => {
      const value = routerState[key]
      if (key === 'current') {
        if (key in routerState) sanitized[key] = currentUrl
      } else if (typeof value === 'string') {
        sanitized[key] = sanitizeHistoryUrl(value)
      }
      return sanitized
    },
    { ...routerState }
  )
}

/**
 * Replaces the active callback URL without retaining credentials in the
 * existing Vue Router history state.
 */
export function replaceFederatedLoginCallbackHistory(url: string, history = window.history) {
  history.replaceState(sanitizeFederatedLoginHistoryState(history.state, url), '', url)
}

/**
 * Validates and removes callback data before normal router and tracking startup
 * can observe it. Only a valid access token is persisted, and the callback
 * result is held in memory for App to consume.
 */
export function initializeFederatedLoginCallback({
  pathname,
  search,
  hash,
  setAccessToken,
  replaceUrl,
  storage = getSessionStorage(),
  now = Date.now(),
}: CallbackInitialization): CallbackResult {
  const callback = extractFederatedLoginCallback(search, hash, storage, now)
  pendingCallback = callback.hasCallbackParams ? callback : null

  if (callback.hasCallbackParams) {
    replaceUrl(stripFederatedLoginCallbackParams(pathname, search, hash))
    if (callback.isValid && callback.accessToken) {
      setAccessToken(callback.accessToken)
    }
  }

  return callback
}

/**
 * Returns the pending initialization result once and clears it to prevent
 * callback handling from being replayed during the application lifetime.
 */
export function takeFederatedLoginCallback(): CallbackResult | null {
  const callback = pendingCallback
  pendingCallback = null
  return callback
}
